import TencentCloudChat from '@tencentcloud/chat';
import { chatApiService } from './chatApiService';

const TIM_SDK_APP_ID = 1600136251;

let chat: any = null;
let currentUserId: string | null = null;
let isLoggedIn = false;
let isSDKReady = false;
let loginPromise: Promise<void> | null = null;
let sdkReadyPromise: Promise<void> | null = null;
let sdkReadyResolve: (() => void) | null = null;
const conversationUpdatedCallbacks = new Set<() => void>();
const messageReceivedCallbacks = new Set<(msgs: any[]) => void>();

function resetSDKReadyPromise() {
  sdkReadyPromise = new Promise<void>((resolve) => {
    sdkReadyResolve = resolve;
  });
}

function markSDKReady() {
  isSDKReady = true;
  sdkReadyResolve?.();
  sdkReadyResolve = null;
}

function markSDKNotReady() {
  isLoggedIn = false;
  isSDKReady = false;
  resetSDKReadyPromise();
}

async function waitForSDKReady() {
  if (isSDKReady) {
    return;
  }

  if (!sdkReadyPromise) {
    resetSDKReadyPromise();
  }

  await sdkReadyPromise;
}

async function waitForReady(): Promise<boolean> {
  if (!chat) {
    return false;
  }

  if (isSDKReady) {
    return true;
  }

  if (loginPromise) {
    try {
      await loginPromise;
    } catch {
      return false;
    }
    return isSDKReady;
  }

  if (!isLoggedIn) {
    return false;
  }

  await waitForSDKReady();
  return isSDKReady;
}

function ensureSDK() {
  if (chat) return;
  chat = TencentCloudChat.create({ SDKAppID: TIM_SDK_APP_ID });
  chat.setLogLevel(1);
  resetSDKReadyPromise();

  chat.on(TencentCloudChat.EVENT.SDK_READY, () => {
    markSDKReady();
  });

  chat.on(TencentCloudChat.EVENT.SDK_NOT_READY, () => {
    markSDKNotReady();
  });

  chat.on(TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED, (event: any) => {
    conversationUpdatedCallbacks.forEach((callback) => {
      void Promise.resolve(callback()).catch((error) => {
        console.error('Conversation updated callback failed:', error);
      });
    });
  });

  chat.on(TencentCloudChat.EVENT.MESSAGE_RECEIVED, (event: any) => {
    messageReceivedCallbacks.forEach((callback) => {
      void Promise.resolve(callback(event.data)).catch((error) => {
        console.error('Message received callback failed:', error);
      });
    });
  });

  chat.on(TencentCloudChat.EVENT.KICKED_OUT, () => {
    currentUserId = null;
    markSDKNotReady();
  });
}

export const timService = {
  async login(userId: string) {
    ensureSDK();

    if (isLoggedIn && isSDKReady && currentUserId === userId) {
      return;
    }

    if (loginPromise) {
      await loginPromise;
      if (isLoggedIn && isSDKReady && currentUserId === userId) {
        return;
      }
    }

    if (currentUserId && currentUserId !== userId && (isLoggedIn || isSDKReady)) {
      await this.logout();
    }

    loginPromise = (async () => {
      markSDKNotReady();
      const sigData = await chatApiService.getUserSig();
      await chat.login({ userID: userId, userSig: sigData.userSig });
      await waitForSDKReady();
      currentUserId = userId;
      isLoggedIn = true;
    })();

    try {
      await loginPromise;
    } finally {
      loginPromise = null;
    }
  },

  async logout() {
    if (!chat) {
      currentUserId = null;
      markSDKNotReady();
      return;
    }

    try {
      if (isLoggedIn || isSDKReady || currentUserId) {
        await chat.logout();
      }
    } finally {
      currentUserId = null;
      loginPromise = null;
      markSDKNotReady();
    }
  },

  onConversationUpdated(cb: () => void) {
    conversationUpdatedCallbacks.add(cb);
    return () => {
      conversationUpdatedCallbacks.delete(cb);
    };
  },

  onMessageReceived(cb: (msgs: any[]) => void) {
    messageReceivedCallbacks.add(cb);
    return () => {
      messageReceivedCallbacks.delete(cb);
    };
  },

  async getConversationList(): Promise<any[]> {
    if (!(await waitForReady())) return [];
    const res = await chat.getConversationList();
    return res.data?.conversationList || [];
  },

  async getMessageList(conversationID: string, nextReqMessageID?: string): Promise<{ messages: any[]; isCompleted: boolean; nextReqMessageID: string }> {
    if (!(await waitForReady())) {
      return { messages: [], isCompleted: true, nextReqMessageID: '' };
    }
    const res = await chat.getMessageList({ conversationID, nextReqMessageID, count: 15 });
    return {
      messages: res.data?.messageList || [],
      isCompleted: res.data?.isCompleted ?? true,
      nextReqMessageID: res.data?.nextReqMessageID || '',
    };
  },

  async getFullMessageList(conversationID: string): Promise<any[]> {
    const allMessages: any[] = [];
    let nextReqMessageID = '';
    let isCompleted = false;
    let pageGuard = 0;

    while (!isCompleted && pageGuard < 40) {
      const res = await this.getMessageList(conversationID, nextReqMessageID || undefined);
      allMessages.push(...res.messages);
      isCompleted = res.isCompleted;
      nextReqMessageID = res.nextReqMessageID;
      pageGuard += 1;

      if (!nextReqMessageID) {
        break;
      }
    }

    return allMessages.sort((left, right) => (left.time || 0) - (right.time || 0));
  },

  async sendTextMessage(conversationID: string, text: string): Promise<any> {
    if (!(await waitForReady())) return null;
    const to = conversationID.replace(/^C2C/, '');
    const message = chat.createTextMessage({
      to,
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: { text },
    });
    const res = await chat.sendMessage(message);
    return res.data?.message;
  },

  async sendCustomMessage(conversationID: string, data: Record<string, any>): Promise<any> {
    if (!(await waitForReady())) return null;
    const to = conversationID.replace(/^C2C/, '');
    const message = chat.createCustomMessage({
      to,
      conversationType: TencentCloudChat.TYPES.CONV_C2C,
      payload: {
        data: JSON.stringify(data),
        description: data.type || 'custom',
        extension: '',
      },
    });
    const res = await chat.sendMessage(message);
    return res.data?.message;
  },

  async setMessageRead(conversationID: string) {
    if (!(await waitForReady())) return;
    await chat.setMessageRead({ conversationID });
  },

  async clearHistory(conversationID: string) {
    if (!(await waitForReady())) return;
    await chat.clearHistoryMessage(conversationID);
  },

  getC2CConversationID(userId: string): string {
    return `C2C${userId}`;
  },

  getSDK() {
    return chat;
  },

  isReady() {
    return isLoggedIn && isSDKReady;
  },
};
